<script setup>
import { ref, useTemplateRef } from 'vue'

const numToHex = (n) => {
  const hex = n.toString(16)
  return hex.length == 1 ? "0" + hex : hex
}

const rgbToHex = (r, g, b) => {
  return `${numToHex(r)}${numToHex(g)}${numToHex(b)}`
}

const el = document.getElementById('app')
const styles = window.getComputedStyle(el)
const colorMap = {}
const palette = {}
for (let i = 0; i < styles.length; i++) {
  if (styles[i].startsWith('--md')) {
    const color = styles.getPropertyValue(styles[i])
    let rgb
    let hex
    
    colorMap[styles[i]] = {
      bg: `background-color: ${color}`,
    }

    if (color.startsWith('rgb')) {
      rgb = color.match(/rgba?\((\d+),?\s*(\d+),?\s*(\d+)\)/)
      colorMap[styles[i]].r = parseInt(rgb[1], 10)
      colorMap[styles[i]].g = parseInt(rgb[2], 10)
      colorMap[styles[i]].b = parseInt(rgb[3], 10)
      colorMap[styles[i]].hex = rgbToHex(colorMap[styles[i]].r, colorMap[styles[i]].g, colorMap[styles[i]].b)
    } else {
      hex = color
      colorMap[styles[i]].hex = color
    }
  } else if (styles[i].startsWith('--color')) {
    const color = styles.getPropertyValue(styles[i])
    palette[styles[i]] = {
      bg: `background-color: ${color}`,
      hex: color
    }
  }
}

const colors = ref(colorMap)
const bgColor = ref('black')
const colorMapEls = useTemplateRef('colorMapEls')

const getColorHex = (key) => {
  return '#' + colorMap[`--md-sys-color-${key}`].hex
}

const getColorRGB = (key) => {
  const r = colorMap[`--md-sys-color-${key}`].r
  const g = colorMap[`--md-sys-color-${key}`].g
  const b = colorMap[`--md-sys-color-${key}`].b
  return `rgb(${r} ${g} ${b})`
}

const invertBG = () => {
  if (bgColor.value === 'black') bgColor.value = 'white'
  else if (bgColor.value === 'white') bgColor.value = 'black'
}

const onRGBChange = (key) => {
  const rgb = `rgb(0,0,0)`

  el.style.setProperty(key, rgb)
}

const onHexChange = (key) => {
  const hex = ''
}

</script>

<template>
  <div class="color-palette-wrapper">
    <div class="color-palette" :style="{ 'background-color': bgColor }">
      <div class="background-flipper">
        <label for="bg-flipper">Invert Background</label>
        <input type="checkbox" id="bg-flipper" @click="invertBG">
      </div>

      <div class="row-one">
        <div class="colors-group">
          <div class="big primary">
            <div>Primary</div>
            <div>{{getColorHex('primary')}}</div>
          </div>
          <div class="big secondary">
            Secondary
            <div>{{getColorHex('secondary')}}</div>
          </div>
          <div class="big tertiary">Tertiary
            <div>{{getColorHex('tertiary')}}</div>
          </div>

          <div class="small on-primary">On Primary
            <div>{{getColorHex('on-primary')}}</div>
          </div>
          <div class="small on-secondary">On Secondary
            <div>{{getColorHex('on-secondary')}}</div>
          </div>
          <div class="small on-tertiary">On Tertiary
            <div>{{getColorHex('on-tertiary')}}</div>
          </div>

          <div class="big primary-container">Primary Container
            <div>{{getColorHex('primary-container')}}</div>
          </div>
          <div class="big secondary-container">Secondary Container
            <div>{{getColorHex('secondary-container')}}</div>
          </div>
          <div class="big tertiary-container">Tertiary Container
            <div>{{getColorHex('tertiary-container')}}</div>
          </div>

          <div class="small on-primary-container">On Primary Container
            <div>{{getColorHex('on-primary-container')}}</div>
          </div>
          <div class="small on-secondary-container">On Secondary Container
            <div>{{getColorHex('on-secondary-container')}}</div>
          </div>
          <div class="small on-tertiary-container">On Tertiary Container
            <div>{{getColorHex('on-tertiary-container')}}</div>
          </div>
        </div>

        <div class="errors">
          <div class="big error">Error
            <div>{{getColorHex('error')}}</div>
          </div>
          <div class="small on-error">On Error
            <div>{{getColorHex('on-error')}}</div>
          </div>
          <div class="big error-container">Error Container
            <div>{{getColorHex('error-container')}}</div>
          </div>
          <div class="small on-error-container">On Error Container
            <div>{{getColorHex('on-error-container')}}</div>
          </div>
        </div>
      </div>

      <div class="row-two">
        <div class="surfaces">
          <div class="big surface-dim">Surface Dim
            <div>{{getColorHex('surface-dim')}}</div>
          </div>
          <div class="big surface">Surface
            <div>{{getColorHex('surface')}}</div>
          </div>
          <div class="big surface-bright">Surface Bright
            <div>{{getColorHex('surface-bright')}}</div>
          </div>

          <div class="med surface-container-lowest">Surface Container Lowest
            <div>{{getColorHex('surface-container-lowest')}}</div>
          </div>
          <div class="med surface-container-low">Surface Container Low
            <div>{{getColorHex('surface-container-low')}}</div>
          </div>
          <div class="med surface-container">Surface Container
            <div>{{getColorHex('surface-container')}}</div>
          </div>
          <div class="med surface-container-high">Surface Container High
            <div>{{getColorHex('surface-container-high')}}</div>
          </div>
          <div class="med surface-container-highest">Surface Container Highest
            <div>{{getColorHex('surface-container-highest')}}</div>
          </div>

          <div class="smaller on-surface">On Surface
            <div>{{getColorHex('on-surface')}}</div>
          </div>

          <div class="smaller on-surface-variant">On Surface Var
            <div>{{getColorHex('on-surface-variant')}}</div>
          </div>

          <div class="smaller outline">Outline
            <div>{{getColorHex('outline')}}</div>
          </div>

          <div class="smaller outline-variant">Outline Var
            <div>{{getColorHex('outline-variant')}}</div>
          </div>
        </div>

        <div class="inverse-wrapper">
          <div class="big inverse-surface">Inverse Surface
            <div>{{getColorHex('inverse-surface')}}</div>
          </div>
          <div class="small inverse-on-surface">Inverse On Surface
            <div>{{getColorHex('inverse-on-surface')}}</div>
          </div>
          <div class="small inverse-primary">Inverse Primary
            <div>{{getColorHex('inverse-primary')}}</div>
          </div>

          <div class="shadows">
            <div class="smallest scrim">Scrim
              <div>{{getColorHex('scrim')}}</div>
            </div>

            <div class="smallest shadow">Shadow
              <div>{{getColorHex('shadow')}}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="color-palette-values">
      <div>
        <div v-for="v,k in colors" key="k" class="color-value-cont" ref="colorMapEls">
          <span class="color-name">{{k}}</span>
          <div class="color-value" :id="k">
            <input :value="v.hex" @input="onHexChange(k)">
          </div>
          <div class="color-sample" :style="v.bg" ></div>
        </div>
      </div>
    </div>
  </div>

  <div class="full-color-palette">
    <div v-for="color, k in palette" :key="k">
      <div class="color-block" :style="{backgroundColor: color.hex}">{{k}}</div>
      <div class="hex-code">{{color.hex}}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.color-palette-wrapper {
  display: flex;
  height: 650px;
}

.color-palette {
  padding: 20px;
  overflow: auto;

  .colors-group {
    display: flex;
    flex-wrap: wrap;
    width: 760px;
    gap: 2px;
    justify-content: space-between;
    
    .primary { background-color: color(primary); color: color(on-primary); }
    .on-primary { background-color: color(on-primary); color: color(primary); }
    .primary-container { background-color: color(primary-container); color: color(on-primary-container); }
    .on-primary-container { background-color: color(on-primary-container); color: color(primary-container); }

    .secondary { background-color: color(secondary); color: color(on-secondary); }
    .on-secondary { background-color: color(on-secondary); color: color(secondary); }
    .secondary-container { background-color: color(secondary-container); color: color(on-secondary-container); }
    .on-secondary-container { background-color: color(on-secondary-container); color: color(secondary-container); }

    .tertiary { background-color: color(tertiary); color: color(on-tertiary); }
    .on-tertiary { background-color: color(on-tertiary); color: color(tertiary); }
    .tertiary-container { background-color: color(tertiary-container); color: color(on-tertiary-container); }
    .on-tertiary-container { background-color: color(on-tertiary-container); color: color(tertiary-container); }
  }

  .errors {
    display: flex;
    flex-direction: column;
    gap: 2px;

    .error { background-color: color(error); color: color(on-error); }
    .on-error { background-color: color(on-error); color: color(error); }
    .error-container { background-color: color(error-container); color: color(on-error-container); }
    .on-error-container { background-color: color(on-error-container); color: color(error-container); }
  }

  .row-one,
  .row-two {
    display: flex;
  }
  
  .surfaces {
    margin-top: 20px;
    display: flex;
    flex-wrap: wrap;
    width: 760px;
    gap: 2px;
    justify-content: space-between;

    .surface-wrapper,
    .containers,
    .on-surface-wrapper {
      display: flex;
    }

    .surface-dim { background-color: color(surface-dim); color: color(on-surface); }
    .surface{ background-color: color(surface); color: color(on-surface); }
    .surface-bright{ background-color: color(surface-bright); color: color(on-surface); }

    .surface-container-lowest { background-color: color(surface-container-lowest); color: color(on-surface); }
    .surface-container-low{ background-color: color(surface-container-low); color: color(on-surface); }
    .surface-container{ background-color: color(surface-container); color: color(on-surface); }
    .surface-container-high { background-color: color(surface-container-high); color: color(on-surface); }
    .surface-container-highest { background-color: color(surface-container-highest); color: color(on-surface); }

    .on-surface { background-color: color(on-surface); color: color(surface); }
    .on-surface-variant { background-color: color(on-surface-variant); color: color(surface); }
    .outline { background-color: color(outline); color: color(surface); }
    .outline-variant { background-color: color(outline-variant); color: color(on-surface); }
  }

  .inverse-wrapper {
    margin-top: 20px;
    margin-left: 20px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    .inverse-surface { background-color: color(inverse-surface); color: color(inverse-on-surface); }
    .inverse-on-surface { background-color: color(inverse-on-surface); color: color(inverse-surface); }
    .inverse-primary { background-color: color(inverse-primary); color: color(inverse-surface); }

    .shadows {
      display: flex;
      justify-content: space-between;

      .scrim { background-color: color(scrim); color: color(inverse-surface); }
      .shadow { background-color: color(shadow); color: color(inverse-surface); }
    }
  }

  .big {
    padding: 5px;
    width: 250px;
    height: 100px;
  }

  .med {
    padding: 5px;
    width: 150px;
    height: 100px;
  }

  .small {
    padding: 5px;
    width: 250px;
    height: 50px;
  }

  .smaller {
    padding: 5px;
    height: 50px;
    width: 188px;
  }

  .smallest {
    padding: 5px;
    height: 50px;
    width: 123px;
  }

  .errors {
    margin-left: 20px;
  }
}

.color-palette-values {
  display: flex;
  flex-direction: column;
  overflow: auto;
  width: 580px;
  padding: 5px;
  margin-left: 20px;

  .color-value-cont {
    display: flex;
    align-items: center;
    margin: 5px;

    .color-name {
      font-weight: 600;
      margin-right: 5px;
    }

    .color-value {
      margin-left: auto;
      display: flex;

      input {
        //width: 45px;
      }
    }

    .color-sample {
      width: 40px;
      height: 40px;
      margin: 5px;
    }
  }
}

.full-color-palette {
  display: flex;
  flex-wrap: wrap;
  width: 1560px;
  overflow: auto;
  height: calc(100% - 650px);

  .color-block {
    margin: 10px;
    height: 100px;
    width: 100px;
  }
}

</style>
